import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { Image } from "antd";

import { BSBookInfo, getTagConfig } from "sketon/service/data/BSBookInfo";
import { useLoadBooklist } from "sketon/service/storage/booklistLoader";
import { DEFAULT_BOOK_COVER } from "sketon/ui/theme/DefaultThemeSetting";
import { isEmpty } from "sketon/utility/StringUtils";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { SCREEN_SM } from "sketon/ui/theme";
import { useIsInSMSize } from "sketon/utility/DeviceInfo";
import { BSBackView } from "sketon/ui/component/BackView";
import { BSMainPage } from "../main";
import { getNavPath } from "sketon/app/RouterConfig";
import { toCoverProxyPath } from "sketon/const";
import Footer from "sketon/ui/component/Footer";
import SpaceHolder from "sketon/ui/component/Space";

const SytledWrapper = styled.div`
  width: 100%;

  display: flex;
  flex-flow: column;
  justify-content: flex-start;
  align-items: center;

  min-height: 100vh;

  @media (max-width: ${SCREEN_SM}) {
    padding: 0 0.4rem;
    margin-top: 0rem;
  }

  @media (min-width: ${SCREEN_SM}) {
    position: relative;
  }
`;

const StyledContainerDiv = styled.div`
  width: 1000px;
  margin-top: 0;
  margin-left: 0;
  margin-bottom: 80px;

  display: flex;
  flex-flow: column;
  justify-content: flex-start;
  align-items: center;

  @media (max-width: ${SCREEN_SM}) {
    width: 100%;
    min-height: 100vh;
    margin-bottom: 0;
  }
`;

const StyledBackDiv = styled.div`
  width: 100%;
  height: 80px;
  margin-top: 0;
  margin-left: 0;
  padding: 0 0 0 0;
  border-radius: 0;
  display: flex;
  flex-flow: column;
  align-items: flex-start;
  justify-content: flex-start;
`;

const StyledSectionTitle = styled.div`
  width: 100%;
  margin-top: 30px;
  margin-left: 0;
  padding: 0;
  border-radius: 0;

  text-align: left;

  font-size: 24px;
  line-height: 50px;
  color: rgba(17, 17, 17, 0.8);
  text-align: left;
  font-weight: bold;

  @media (max-width: ${SCREEN_SM}) {
    font-size: 0.45rem;
    line-height: 0.55rem;
  }
`;

const StyledBookItem = styled.div`
  width: 100%;
  min-height: 155px;
  margin-top: 0;
  margin-left: 0;

  display: flex;
  flex-flow: row;
  justify-content: flex-start;
  align-items: center;

  @media (max-width: ${SCREEN_SM}) {
    justify-content: space-between;
  }
`;

const StyledBookCover = styled.div`
  width: 300px;
  margin-top: 0;
  margin-left: 0;
  padding: 0 0 0 0;
  overflow: hidden;
  position: relative;

  @media (max-width: ${SCREEN_SM}) {
    width: 4.5rem;
  }
`;

const StyledBookCoverMask = styled.div`
  position: absolute;
  background: transparent;
  top: 0px;
  bottom: 0px;
  left: 0px;
  right: 0px;
  border: 2px solid #ffffff;
  z-index: 2;
`;

const StyledStampTag = styled.div<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  margin-top: 12px;
  padding: 3px 12px;
  border-radius: 4px;
  background: ${(props) => props.$color + "1A"};
  color: ${(props) => props.$color};
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 2px;
  white-space: nowrap;

  @media (max-width: ${SCREEN_SM}) {
    margin-top: 0.12rem;
    padding: 2px 0.14rem;
    font-size: 0.26rem;
    letter-spacing: 1px;
  }
`;

const BookNameWithTag: React.FC<{
  name: string;
  tagColor: string;
  tagLabel: string;
}> = ({ name, tagColor, tagLabel }) => {
  return (
    <div style={{ width: "100%" }}>
      <StyledBookNameDiv>{name}</StyledBookNameDiv>
      <StyledStampTag $color={tagColor}>{tagLabel}</StyledStampTag>
    </div>
  );
};

const StyledBookRight = styled.div`
  margin-top: 0;
  margin-left: 40px;
  padding: 0 0 0 0;
  border-radius: 0;

  display: flex;
  flex-flow: column;
  justify-content: center;
  align-items: left;
  position: relative;

  @media (max-width: ${SCREEN_SM}) {
    margin-left: 20px;
  }
`;

const StyledBookNameDiv = styled.div`
  margin-top: 0;
  margin-left: 0;
  padding: 0 0 0 0;

  border-radius: 0;

  text-align: left;

  font-size: 40px;
  line-height: 50px;
  color: #111111;
  text-align: left;
  font-weight: bold;

  display: inline-block;
  width: fit-content;
  max-width: 100%;

  @media (max-width: ${SCREEN_SM}) {
    font-size: 0.6rem;
    line-height: 0.7rem;
  }
`;

const StyledBookSubNameDiv = styled.div`
  margin-top: 10px;
  margin-left: 0;
  padding: 0 0 0 0;

  border-radius: 0;

  text-align: left;

  font-size: 28px;
  line-height: 35px;
  color: #111111;
  text-align: left;
  font-weight: normal;

  @media (max-width: ${SCREEN_SM}) {
    font-size: 0.4rem;
    line-height: 0.44rem;
  }
`;

const StyledBookISBNDiv = styled.div`
  margin-top: 0px;
  margin-left: 0;
  padding: 0 0 0 0;

  border-radius: 0;

  text-align: left;

  font-size: 16px;
  line-height: 28px;
  color: rgba(17, 17, 17, 0.5);
  text-align: left;
  font-weight: normal;

  @media (max-width: ${SCREEN_SM}) {
    margin-top: 0.1rem;
    font-size: 0.2rem;
    line-height: 0.3rem;
  }
`;

const StyledTagSection = styled.div`
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledTagLabel = styled.span`
  font-size: 16px;
  color: #999999;

  @media (max-width: ${SCREEN_SM}) {
    font-size: 0.24rem;
  }
`;

const StyledDetailTag = styled.span<{ $color: string; $bgColor: string }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 16px;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 500;
  background: ${(props) => props.$bgColor};
  color: ${(props) => props.$color};

  @media (max-width: ${SCREEN_SM}) {
    font-size: 0.24rem;
    padding: 2px 0.2rem;
  }
`;

const StyledBookAuthorDiv = styled.div`
  margin-top: 30px;
  margin-left: 0;
  padding: 0 0 0 0;
  border-radius: 0;

  text-align: left;

  font-size: 20px;
  line-height: 30px;
  color: #999999;
  text-align: left;
  font-weight: normal;

  @media (max-width: ${SCREEN_SM}) {
    margin-top: 0.4rem;
    font-size: 0.3rem;
    line-height: 0.35rem;
  }
`;

const StyledBookAuthorDesc = styled.div`
  margin-top: 10px;
  margin-left: 0;
  padding: 0 0 0 0;
  border-radius: 0;

  text-align: left;

  font-size: 16px;
  line-height: 24px;
  color: #999999;
  text-align: left;
  font-weight: normal;

  white-space: pre-wrap;
`;

export interface BSDetailPageProps {}

export const BSDetailPage: React.FC<BSDetailPageProps> = () => {
  const params = useParams();
  const list = useLoadBooklist();

  const isSMSize = useIsInSMSize();

  const [bookInfo, setBookInfo] = useState<BSBookInfo | undefined>();

  const setupBookInfo = useCallback(
    (isbn: string) => {
      let book: BSBookInfo | undefined = list?.find((item) => {
        return item.isbn === isbn;
      });

      if (book !== undefined) {
        setBookInfo(book);
      }
    },
    [list]
  );

  const navigate = useNavigate();
  const location = useLocation();
  const goBack = useCallback(() => {
    const from = (location.state as { from?: string })?.from;
    navigate(from || getNavPath(BSMainPage.name));
  }, [location.state, navigate]);

  useEffect(() => {
    if (params && list && bookInfo === undefined) {
      setupBookInfo(params.isbn ?? "");
    }
  }, [params, list, bookInfo, setupBookInfo]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const tagCfg = getTagConfig(bookInfo?.tag);

  return (
    <SytledWrapper>
      <StyledContainerDiv>
        {/* {isSMSize || (
          <StyledBackDiv>
            <BSBackView backAction={goBack}></BSBackView>
          </StyledBackDiv>
        )} */}

        <StyledBackDiv>
          <BSBackView backAction={goBack}></BSBackView>
        </StyledBackDiv>
        <StyledBookItem>
          <StyledBookCover>
            <Image
              width={"100%"}
              src={toCoverProxyPath(bookInfo?.cover || "")}
              preview={false}
              fallback={require("assets/images/" + DEFAULT_BOOK_COVER)}
              placeholder={
                <Image
                  preview={false}
                  src={require("assets/images/" + DEFAULT_BOOK_COVER)}
                />
              }
            ></Image>
            <StyledBookCoverMask></StyledBookCoverMask>
          </StyledBookCover>
          <StyledBookRight>
            <StyledBookNameDiv>{bookInfo?.name} </StyledBookNameDiv>
            {isEmpty(bookInfo?.subname) || (
              <StyledBookSubNameDiv>{bookInfo?.subname} </StyledBookSubNameDiv>
            )}

            <StyledBookAuthorDiv>{bookInfo?.author} </StyledBookAuthorDiv>

            <StyledBookISBNDiv>{"ISBN: " + bookInfo?.isbn}</StyledBookISBNDiv>

            <StyledTagSection>
              <StyledTagLabel>分类：</StyledTagLabel>
              <StyledDetailTag $color={tagCfg.color} $bgColor={tagCfg.bgColor}>
                {tagCfg.label}
              </StyledDetailTag>
            </StyledTagSection>
          </StyledBookRight>
        </StyledBookItem>

        {isEmpty(bookInfo?.desc) || (
          <>
            {" "}
            <StyledSectionTitle>简介</StyledSectionTitle>
            <StyledBookAuthorDesc>{bookInfo?.desc}</StyledBookAuthorDesc>
          </>
        )}

        {isEmpty(bookInfo?.author_info) || (
          <>
            <StyledSectionTitle>作者简介</StyledSectionTitle>
            <StyledBookAuthorDesc>{bookInfo?.author_info}</StyledBookAuthorDesc>
          </>
        )}
      </StyledContainerDiv>

      <SpaceHolder
        isVertical={true}
        value={isSMSize ? "0.2rem" : "0xp"}
      ></SpaceHolder>
      <Footer></Footer>
    </SytledWrapper>
  );
};
